import type { GatewayBrowserClient } from "../gateway.ts";
import type { GatewayHelloOk } from "../gateway.ts";
import type { FleetProjectSummary, SessionsListResult } from "../types.ts";
import { resolveParentSessionKeyForFleetProject } from "../chat/fleet-projects-parent-session.ts";
import {
  projectsForChatContext,
  resolveFleetProjectsContextSessionKey,
} from "../chat/fleet-projects-session.ts";
import { resolveAgentIdForSession } from "../chat/resolve-session-agent.ts";

function isUnknownFleetProjectsMethodError(err: unknown): boolean {
  const msg = String(err);
  return msg.includes("unknown method: fleet.projects");
}

export function formatFleetProjectsError(err: unknown): string {
  if (isUnknownFleetProjectsMethodError(err)) {
    return "Fleet Projects needs a newer gateway. Run pnpm build in the Jaisiu repo, then restart the gateway service (e.g. docker restart or relaunch LaunchAgent), and click Refresh.";
  }
  return String(err);
}

export type FleetProjectsState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  sessionKey: string;
  sessionsResult?: SessionsListResult | null;
  hello?: GatewayHelloOk | null;
  agentsList?: { defaultId?: string } | null;
  agentsDefaultId?: string;
  fleetProjectsLoading: boolean;
  fleetProjectsError: string | null;
  fleetProjects: FleetProjectSummary[];
};

function resolveAgentId(state: FleetProjectsState): string {
  const defaultAgentId = state.agentsList?.defaultId ?? state.agentsDefaultId ?? "main";
  return resolveAgentIdForSession(state.sessionKey, defaultAgentId);
}

export async function loadFleetProjects(state: FleetProjectsState) {
  if (!state.client || !state.connected) {
    state.fleetProjects = [];
    state.fleetProjectsError = null;
    return;
  }
  const agentId = resolveAgentId(state);
  state.fleetProjectsLoading = true;
  state.fleetProjectsError = null;
  try {
    const originSessionKey =
      resolveFleetProjectsContextSessionKey(state.sessionKey, state.fleetProjects) || undefined;
    const res = await state.client.request<{
      agentId: string;
      projects: FleetProjectSummary[];
      total?: number;
      appliedLimit?: number;
      truncated?: boolean;
    }>("fleet.projects.list", { agentId, originSessionKey, limit: 30 });
    state.fleetProjects = Array.isArray(res?.projects) ? res.projects : [];
  } catch (err) {
    state.fleetProjectsError = formatFleetProjectsError(err);
    state.fleetProjects = [];
  } finally {
    state.fleetProjectsLoading = false;
  }
}

export async function createFleetProject(
  state: FleetProjectsState,
  name: string,
): Promise<FleetProjectSummary | null> {
  if (!state.client || !state.connected) {
    return null;
  }
  const agentId = resolveAgentId(state);
  const trimmed = name.trim();
  if (!trimmed) {
    return null;
  }
  try {
    const parentSessionKey = resolveParentSessionKeyForFleetProject({
      sessionKey: state.sessionKey,
      sessions: state.sessionsResult ?? null,
      hello: state.hello ?? null,
      fleetProjects: state.fleetProjects,
    });
    const res = await state.client.request<{
      project: FleetProjectSummary;
      sessionKey: string;
      sessionId: string;
      label: string;
    }>("fleet.projects.create", {
      agentId,
      name: trimmed,
      parentSessionKey,
    });
    if (res?.project) {
      state.fleetProjects = [
        res.project,
        ...state.fleetProjects.filter((p) => p.id !== res.project.id),
      ];
    }
    return res?.project ?? null;
  } catch (err) {
    state.fleetProjectsError = formatFleetProjectsError(err);
    return null;
  }
}

export async function updateFleetProject(
  state: FleetProjectsState,
  projectId: string,
  name: string,
): Promise<FleetProjectSummary | null> {
  if (!state.client || !state.connected) {
    return null;
  }
  const agentId = resolveAgentId(state);
  try {
    const res = await state.client.request<{ project: FleetProjectSummary }>(
      "fleet.projects.update",
      {
        agentId,
        projectId,
        name: name.trim(),
      },
    );
    if (res?.project) {
      state.fleetProjects = state.fleetProjects.map((p) =>
        p.id === res.project.id ? res.project : p,
      );
    }
    return res?.project ?? null;
  } catch (err) {
    state.fleetProjectsError = formatFleetProjectsError(err);
    return null;
  }
}

export async function deleteFleetProject(
  state: FleetProjectsState,
  projectId: string,
): Promise<{ sessionKey: string } | null> {
  if (!state.client || !state.connected) {
    return null;
  }
  const agentId = resolveAgentId(state);
  try {
    const res = await state.client.request<{
      ok: true;
      projectId: string;
      sessionKey: string;
    }>("fleet.projects.delete", {
      agentId,
      projectId,
      confirm: true,
    });
    state.fleetProjects = state.fleetProjects.filter((p) => p.id !== projectId);
    return res ? { sessionKey: res.sessionKey } : null;
  } catch (err) {
    state.fleetProjectsError = formatFleetProjectsError(err);
    return null;
  }
}

export async function discoverFleetProjects(state: FleetProjectsState): Promise<number> {
  if (!state.client || !state.connected) {
    return 0;
  }
  const agentId = resolveAgentId(state);
  try {
    const res = await state.client.request<{
      agentId: string;
      registered: FleetProjectSummary[];
    }>("fleet.projects.discover", { agentId });
    const registered = Array.isArray(res?.registered) ? res.registered : [];
    if (registered.length > 0) {
      const byId = new Map(state.fleetProjects.map((p) => [p.id, p]));
      for (const p of registered) {
        byId.set(p.id, p);
      }
      state.fleetProjects = [...byId.values()].sort((a, b) => b.updatedAtMs - a.updatedAtMs);
    }
    return registered.length;
  } catch (err) {
    state.fleetProjectsError = formatFleetProjectsError(err);
    return 0;
  }
}

export function resolveActiveFleetProject(
  state: FleetProjectsState & { chatActiveProjectId?: string | null },
): FleetProjectSummary | null {
  const key = state.sessionKey.trim();
  const scoped = projectsForChatContext(state.fleetProjects, key);
  const bySession = scoped.find((p) => p.sessionKey === key);
  if (bySession) {
    return bySession;
  }
  const id = state.chatActiveProjectId?.trim();
  if (id) {
    return scoped.find((p) => p.id === id) ?? null;
  }
  return scoped[0] ?? null;
}
